import type { Doc } from "building-types/doc";
import type {
  RoofEdgeHandle,
  RoofHandle,
  RoofSideHandle,
  RoofSystemHandle,
} from "building-types/handles/docEntityHandleTypes";
import type {
  RoofOpeningPlacement,
  Roof,
  RoofDormerPlacement,
  SkylightPlacement,
} from "building-types/roofs/roofSystemTypes";
import type { RoofEdgeOverrides, RoofSide } from "building-types/settings/roofTypes";
import { sortEntitiesBySortIndex } from "common/utils/sortIndex";
import { docGetTableEntity } from "./entity/docGetTableEntity";

export function docGetRoofEdgeOverrides(doc: Doc, { parent, side }: RoofSideHandle): RoofEdgeOverrides | null {
  const roof = docGetTableEntity(doc, parent);

  if (!roof) {
    return null;
  }

  const { roofSideOverrides = {} } = roof;

  return roofSideOverrides[side] ?? null;
}

export function docGetRoofEdge(doc: Doc, { parent, edgeId }: RoofEdgeHandle): RoofEdgeOverrides | null {
  const roof = docGetTableEntity(doc, parent);

  if (!roof) {
    return null;
  }

  const { roofEdgeOverrides = {} } = roof;

  return roofEdgeOverrides[edgeId] ?? {};
}

export function docGetRoofSystemRoofs(doc: Pick<Doc, "roofs">, { id: roofSystemId }: RoofSystemHandle): Roof[] {
  const roofs = Object.values(doc.roofs).filter((x) => x.parentId === roofSystemId);
  return sortEntitiesBySortIndex(roofs);
}

export function docGetRoofDormers(doc: Pick<Doc, "dormers">, { id: roofId }: RoofHandle): RoofDormerPlacement[] {
  return Object.values(doc.dormers)
    .filter((x) => x.parentId === roofId)
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

export function docGetRoofDormerIds(doc: Doc, { id: roofId }: RoofHandle): string[] {
  return Object.keys(doc.dormers)
    .filter((id) => doc.dormers[id].parentId === roofId)
    .sort((a, b) => doc.dormers[a].sortIndex - doc.dormers[b].sortIndex);
}

export function docGetRoofSkylights(doc: Pick<Doc, "skylights">, { id: roofId }: RoofHandle): SkylightPlacement[] {
  return Object.values(doc.skylights)
    .filter((x) => x.parentId === roofId)
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

export function docGetRoofSkylightIds(doc: Doc, { id: roofId }: RoofHandle): string[] {
  return Object.keys(doc.skylights)
    .filter((id) => doc.skylights[id].parentId === roofId)
    .sort((a, b) => doc.skylights[a].sortIndex - doc.skylights[b].sortIndex);
}

export function docGetRoofRoofOpenings(
  doc: Pick<Doc, "roofOpenings">,
  { id: roofId }: RoofHandle,
  side: RoofSide | undefined,
): RoofOpeningPlacement[] {
  return Object.values(doc.roofOpenings)
    .filter((x) => x.parentId === roofId && x.side === side)
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

export function docGetRoofOpeningIds(doc: Doc, { id: roofId }: RoofHandle, side: RoofSide): string[] {
  return Object.keys(doc.roofOpenings)
    .filter((id) => doc.roofOpenings[id].parentId === roofId && doc.roofOpenings[id].side === side)
    .sort((a, b) => doc.roofOpenings[a].sortIndex - doc.roofOpenings[b].sortIndex);
}
